import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import classes from './Dashboard.module.css';

const Dashboard = () => {
    const user = useSelector(state => state.auth.user);
    const streakDays = useSelector(state => state.auth.streakDays);
    const [timeOfDay, setTimeOfDay] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Sample data
    const netWorthTrend = [
        { month: 'Jan', value: 115000 },
        { month: 'Feb', value: 118500 },
        { month: 'Mar', value: 122000 },
        { month: 'Apr', value: 119800 },
        { month: 'May', value: 124500 },
        { month: 'Jun', value: 127350 }
    ];

    const monthlyBreakdown = [
        { category: 'Income', amount: 4500, color: '#10B981', icon: '💰' },
        { category: 'Expenses', amount: 3200, color: '#EF4444', icon: '🛒' },
        { category: 'Investments', amount: 800, color: '#8B5CF6', icon: '📈' },
        { category: 'Savings', amount: 1100, color: '#06B6D4', icon: '🏦' }
    ];

    const recentActivities = [
        { type: 'investment', description: 'S&P 500 ETF Purchase', amount: 500, time: '2 hours ago', icon: '📈' },
        { type: 'expense', description: 'Grocery Shopping', amount: -89.50, time: '5 hours ago', icon: '🛒' },
        { type: 'income', description: 'Freelance Payment', amount: 750, time: '1 day ago', icon: '💼' },
        { type: 'saving', description: 'Emergency Fund Deposit', amount: 200, time: '2 days ago', icon: '🏦' }
    ];

    const aiRecommendations = [
        {
            type: 'tip',
            title: 'Smart Tip',
            description: 'Consider increasing your 401(k) contribution by 2% to maximize your employer match and reduce taxable income.',
            icon: '💡',
            color: 'blue'
        },
        {
            type: 'goal',
            title: 'Goal Progress',
            description: "You're on track to reach your emergency fund goal 2 months early! Consider setting a new savings target.",
            icon: '🎯',
            color: 'emerald'
        },
        {
            type: 'attention',
            title: 'Attention',
            description: 'Your dining out expenses increased by 35% this month. Consider meal planning to optimize your budget.',
            icon: '⚠️',
            color: 'amber'
        }
    ];

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour < 12) setTimeOfDay('Good morning');
        else if (hour < 18) setTimeOfDay('Good afternoon');
        else setTimeOfDay('Good evening');
    }, []);

    const handleSync = () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 2000);
    };

    const handleQuickAction = (action) => {
        console.log(`Quick action: ${action}`);
    };

    return (
        <Layout>
            <div className={classes.dashboard}>
                {/* Hero Welcome Section */}
                <div className={classes.heroSection}>
                    <div className={classes.heroContent}>
                        <div className={classes.welcomeContent}>
                            <h1 className={classes.welcomeTitle}>
                                {timeOfDay}, {user?.first_name || 'Alex'}!
                            </h1>
                            <p className={classes.welcomeSubtitle}>
                                Welcome back to your financial command center
                            </p>

                            {/* Streak Achievement */}
                            <div className={classes.streakBadge}>
                                <div className={classes.streakInfo}>
                                    <span className={classes.streakIcon}>🔥</span>
                                    <span className={classes.streakText}>{streakDays} Day Streak</span>
                                </div>
                                <div className={classes.streakDescription}>
                                    No overspending! Keep the momentum going!
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions Panel */}

                    </div>
                    <div className={classes.heroDecorations}></div>
                </div>

                {/* Main Content */}
                <div className={classes.mainContent}>
                    {/* Net Worth Section */}
                    <div className={classes.contentGrid}>
                        <div className={classes.netWorthSection}>
                            <div className={classes.premiumCard}>
                                <div className={classes.cardHeader}>
                                    <div>
                                        <h2 className={classes.cardTitle}>Net Worth</h2>
                                        <p className={classes.cardSubtitle}>Total assets minus liabilities</p>
                                    </div>
                                    <div className={classes.growthBadge}>
                                        +12.5% this month
                                    </div>
                                </div>

                                <div className={classes.netWorthValue}>
                                    <div className={classes.netWorthAmount}>
                                        ${user?.net_worth || '127,350'}
                                    </div>
                                    <p className={classes.lastSynced}>Last synced 3 hours ago</p>
                                </div>

                                <div className={classes.trendChart}>
                                    <div className={classes.chartArea}>
                                        {netWorthTrend.map((point, index) => (
                                            <div
                                                key={index}
                                                className={classes.chartPoint}
                                                style={{
                                                    left: `${(index / (netWorthTrend.length - 1)) * 100}%`,
                                                    bottom: `${((point.value - 115000) / 12350) * 100}%`
                                                }}
                                            ></div>
                                        ))}
                                        <div className={classes.chartLine}></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar Cards */}
                        <div className={classes.sidebarCards}>
                            <div className={classes.savingsCard}>
                                <div className={classes.savingsHeader}>
                                    <span className={classes.savingsIcon}>💎</span>
                                    <h3 className={classes.savingsTitle}>Monthly Savings</h3>
                                </div>
                                <div className={classes.savingsAmount}>$2,840</div>
                                <div className={classes.savingsChange}>
                                    <span>+8.3% vs last month</span>
                                    <span className={classes.changeArrow}>↗</span>
                                </div>
                            </div>

                            <div className={classes.peerCard}>
                                <h3 className={classes.peerTitle}>Peer Comparison</h3>
                                <div className={classes.peerContent}>
                                    <div className={classes.peerIcon}>🎯</div>
                                    <div className={classes.peerStatus}>Above Average</div>
                                    <p className={classes.peerDescription}>
                                        You're saving 15% more than peers in your age group
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Overview */}
                    <div className={classes.overviewGrid}>
                        <div className={classes.monthlyOverview}>
                            <div className={classes.premiumCard}>
                                <h2 className={classes.cardTitle}>This Month Overview</h2>
                                <div className={classes.overviewGrid}>
                                    {monthlyBreakdown.map((item, index) => (
                                        <div key={index} className={classes.overviewItem}>
                                            <div
                                                className={classes.overviewIcon}
                                                style={{ backgroundColor: item.color }}
                                            >
                                                {item.icon}
                                            </div>
                                            <h4 className={classes.overviewLabel}>{item.category}</h4>
                                            <p className={classes.overviewAmount}>
                                                ${item.amount.toLocaleString()}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={classes.recentActivity}>
                            <div className={classes.premiumCard}>
                                <h3 className={classes.cardTitle}>Recent Activity</h3>
                                <div className={classes.activityList}>
                                    {recentActivities.map((activity, index) => (
                                        <div key={index} className={classes.activityItem}>
                                            <div className={classes.activityIcon}>{activity.icon}</div>
                                            <div className={classes.activityContent}>
                                                <div className={classes.activityDescription}>{activity.description}</div>
                                                <div className={classes.activityTime}>{activity.time}</div>
                                            </div>
                                            <div className={`${classes.activityAmount} ${activity.amount > 0 ? classes.positive : classes.negative}`}>
                                                {activity.amount > 0 ? '+' : ''}${Math.abs(activity.amount)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Recommendations */}
                    <div className={classes.aiSection}>
                        <div className={classes.premiumCard}>
                            <div className={classes.aiHeader}>
                                <div className={classes.aiTitleSection}>
                                    <div className={classes.aiIconWrapper}>
                                        <span className={classes.aiIcon}>🤖</span>
                                    </div>
                                    <div>
                                        <h2 className={classes.aiTitle}>AI Financial Advisor</h2>
                                        <p className={classes.aiSubtitle}>Personalized insights and recommendations</p>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.recommendationsGrid}>
                                {aiRecommendations.map((rec, index) => (
                                    <div key={index} className={`${classes.recommendationCard} ${classes[rec.color]}`}>
                                        <div className={classes.recHeader}>
                                            <span className={classes.recIcon}>{rec.icon}</span>
                                            <h3 className={classes.recTitle}>{rec.title}</h3>
                                        </div>
                                        <p className={classes.recDescription}>
                                            {rec.description}
                                        </p>
                                        <button className={classes.recButton}>
                                            Learn More →
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;